import { getFormattedDate } from '@finn/platform-modules/ssr-dates';
import { IntlShape } from '@finn/ui-utils';
import { toUniversalTime } from '@finn/ui-utils/ssr';
import dayjs from 'dayjs';

import { FUEL, GenericVehicleDetails, VehicleSize } from '../../core/types';

export const isElectricVehicle = (fuel?: string) =>
  fuel === FUEL.ElectricDE || fuel === FUEL.ElectricEN;

export const isVehicleComingSoon = (
  vehicle: GenericVehicleDetails
): boolean => {
  // availability overrides coming soon status
  return !vehicle?.availability && !!vehicle?.coming_soon;
};

type GetEarliestDeliveryParams = {
  availableFrom: string;
  deviationInWeeks: number | undefined;
  intl: IntlShape;
};

/**
 * Gets the earliest formatted delivery date.
 *
 * Possible states examples:
 * 17.02.2025 (exact date, no deviation)
 * February 2025 (deviation, same year & month)
 * February/March 2025 (deviation, same year, different month)
 * February 2024/March 2025 (deviation, different year & month)
 */
export const getEarliestDelivery = ({
  availableFrom,
  deviationInWeeks,
  intl,
}: GetEarliestDeliveryParams): string => {
  const earliestDate = toUniversalTime(new Date(availableFrom));

  if (!deviationInWeeks) {
    return getFormattedDate(earliestDate, 'DD.MM.YYYY');
  }
  const availableFromDeviationDate = dayjs(earliestDate).add(
    deviationInWeeks,
    'week'
  );

  const earliestMonth = earliestDate.getMonth();
  const deviatedMonth = availableFromDeviationDate.month();
  const earliestYear = earliestDate.getFullYear();
  const deviatedYear = availableFromDeviationDate.year();

  const sameMonth = earliestMonth === deviatedMonth;
  const sameYear = earliestYear === deviatedYear;

  const earliestMonthName = intl.formatMessage(
    `general.months.${earliestMonth}`
  );

  // Display max. 1 month deviation to avoid unnecessary uncertainty
  const monthNextToEarliest = (earliestMonth + 1) % 12;
  const deviatedMonthName = intl.formatMessage(
    `general.months.${monthNextToEarliest}`
  );

  if (!sameYear) {
    return `${earliestMonthName} ${earliestYear}/${deviatedMonthName} ${deviatedYear}`;
  }

  if (!sameMonth) {
    return `${earliestMonthName}/${deviatedMonthName} ${deviatedYear}`;
  }

  return `${earliestMonthName} ${earliestYear}`;
};

export const getEquipmentLine = (vehicle: GenericVehicleDetails) => {
  return vehicle.trim_name;
};

export const getConfigLineLabel = (vehicle?: GenericVehicleDetails): string => {
  return vehicle?.equipment_line ?? '';
};

/**
 * Takes the vehicle's dimensions and calculates a single number that represents its overall size,
 * using the following weights:
 *
 * Length: 50% (most important for visual size perception)
 * Width: 30% (second most important)
 * Height: 20% (least important for visual comparison)
 *
 * @param width - The width of the vehicle in mm.
 * @param height - The height of the vehicle in mm.
 * @param length - The length of the vehicle in mm.
 * @returns The size factor for the vehicle.
 */
export const calculateSizeFactor = (
  width: number,
  height: number,
  length: number
) => {
  return (width * 0.3 + height * 0.2 + length * 0.5) / 1000;
};

const MAX_WIDTH = 800;
const MIN_WIDTH = 600;

// min and max were calculated running calculateSizeFactor with the min and max values of all vehicle sizes
const minFactor = 2.635;
const maxFactor = 3.6235999999999997;

export const getVehicleImageScale = (
  vehicleSize: VehicleSize,
  maxWidth: number = MAX_WIDTH,
  minWidth: number = MIN_WIDTH
) => {
  const sizeFactor = calculateSizeFactor(
    vehicleSize.width_mm,
    vehicleSize.height_mm,
    vehicleSize.length_mm
  );

  // Calculate the scaled width between max and min width
  const scaledWidth = Math.round(
    minWidth +
      ((sizeFactor - minFactor) * (maxWidth - minWidth)) /
        (maxFactor - minFactor)
  );

  return scaledWidth;
};
